import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import useFeedback from '../../hooks/useFeedback';
import FeedbackScreenshots from '../ui/FeedbackScreenshots';
import ForwardFeedbackToIssueDialog from '../ui/ForwardFeedbackToIssueDialog';
import { useAuth } from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const RATING_CONFIG = {
  up: { color: 'success.main', Icon: ThumbUpOutlinedIcon, label: 'Daumen hoch' },
  idea: { color: 'warning.main', Icon: LightbulbOutlinedIcon, label: 'Idee' },
  down: { color: 'error.main', Icon: ThumbDownOutlinedIcon, label: 'Daumen runter' },
};

// Severity levels of the issue management queue, mirroring the backend enum.
const PRIORITY_CONFIG = {
  p1: { label: 'P1', description: 'Kritisch – Betrieb steht', paletteKey: 'error', rank: 1 },
  p2: { label: 'P2', description: 'Hoch – stark eingeschränkt', paletteKey: 'warning', rank: 2 },
  p3: { label: 'P3', description: 'Mittel – Workaround vorhanden', paletteKey: 'info', rank: 3 },
  p4: { label: 'P4', description: 'Niedrig – kleine Störung', paletteKey: 'success', rank: 4 },
  p5: { label: 'P5', description: 'Gering – kosmetisch / Idee', paletteKey: null, rank: 5 },
};

const PRIORITY_KEYS = Object.keys(PRIORITY_CONFIG);

// Untriaged issues sort right after the triaged ones so nothing gets lost.
const UNPRIORITIZED_RANK = PRIORITY_KEYS.length + 1;

function priorityRank(item) {
  return PRIORITY_CONFIG[item?.priority]?.rank ?? UNPRIORITIZED_RANK;
}

function priorityLabel(priority) {
  const cfg = PRIORITY_CONFIG[priority];
  return cfg ? `${cfg.label} – ${cfg.description}` : 'Keine Priorität';
}

// Chip styling per severity; P5 stays neutral so it does not compete visually.
function priorityChipSx(priority) {
  return t => {
    const paletteKey = PRIORITY_CONFIG[priority]?.paletteKey;
    const main = paletteKey ? t.palette[paletteKey].main : t.palette.text.primary;
    return {
      backgroundColor: alpha(main, paletteKey ? 0.2 : 0.08),
      color: paletteKey ? `${paletteKey}.main` : 'text.secondary',
      fontWeight: 'bold',
      fontSize: { xs: '0.7rem', sm: '0.75rem' },
    };
  };
}

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE');
}

// Extracts the first name from a full display name string.
function firstNameOf(fullName) {
  if (!fullName || typeof fullName !== 'string') return '';
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

// Builds the prefilled default reply text shown when an admin starts a new reply.
function buildDefaultResponse(item, responderName) {
  const recipient =
    firstNameOf(item?.user?.name) || firstNameOf(item?.user?.username) || 'zusammen';
  const responder = firstNameOf(responderName);
  const lines = [
    `Hi ${recipient},`,
    '',
    'ist erledigt.',
    '',
    'Danke für dein Feedback und danke, dass du hilfst das Tool zu verbessern!',
    '',
    'LG',
  ];
  if (responder) lines.push(responder);
  return lines.join('\n');
}

export default function FeedbackAdminView() {
  const authCtx = useAuth();
  const {
    getAllFeedback,
    setSolved,
    setResponse,
    addResponseMessage,
    setRejected,
    setInProgress,
    setPriority,
  } = useFeedback();

  // Full management access: process, respond, change status.
  const canManage = useMemo(() => authCtx.checkRightAny('dev:feedback'), [authCtx]);
  // Branch-scoped read-only access: see feedback of own assigned branches only.
  const canRead = useMemo(
    () => canManage || authCtx.checkRightAny('dev:feedback:readAssigned'),
    [canManage, authCtx]
  );
  // Read-only mode is active when the user can see feedback but not manage it.
  const readOnly = canRead && !canManage;
  const didInitialLoadRef = useRef(false);

  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('open'); // open | inProgress | solved | rejected | all
  const [priorityFilter, setPriorityFilter] = useState('all'); // all | none | p1..p5
  const [sourceFilter, setSourceFilter] = useState('all'); // all | general | newFrontend
  const [sortMode, setSortMode] = useState('priority'); // priority | date
  // Id of the issue whose severity dialog is open, plus its draft selection.
  const [priorityDialogId, setPriorityDialogId] = useState(null);
  const [priorityDraft, setPriorityDraft] = useState('');
  const [isSavingPriority, setIsSavingPriority] = useState(false);
  const priorityNoteRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [responseTexts, setResponseTexts] = useState({});
  // Follow-up thread reply composer state (separate from the primary response editor).
  const [replyOpenItems, setReplyOpenItems] = useState(new Set());
  const [replyTexts, setReplyTexts] = useState({});
  // Entry whose "forward to IT ticket" dialog is open.
  const [forwardItem, setForwardItem] = useState(null);

  const viewLabelByKey = useMemo(() => {
    const m = {};
    const menuItems = Array.isArray(authCtx.menuItems) ? authCtx.menuItems : [];
    for (const i of menuItems) {
      if (!i?.link) continue;
      const label = i.groupPath ? `${i.groupPath} / ${i.name}` : i.name;
      m[i.link] = label;
    }
    return m;
  }, [authCtx.menuItems]);

  const load = useCallback(async () => {
    if (!canRead) return;
    setIsLoading(true);
    try {
      const data = await getAllFeedback({ includeSolved: true });
      const arr = Array.isArray(data) ? data : [];
      setAllItems(arr);
      const initialResponseTexts = {};
      for (const item of arr) {
        if (item.response) {
          initialResponseTexts[String(item._id)] = item.response;
        }
      }
      setResponseTexts(prev => ({ ...prev, ...initialResponseTexts }));
    } finally {
      setIsLoading(false);
    }
  }, [canRead, getAllFeedback]);

  useEffect(() => {
    if (!canRead) return;
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    load().catch(() => {});
  }, [canRead, load]);

  const statusFilteredItems = useMemo(() => {
    // Entries created before the sources were split carry no `source`, and the
    // backend has since added further sources for the answers it ported into
    // this queue. Anything that is not the new-management dialog counts as
    // general here — matching sourceCounts below, so no entry can fall out of
    // both buckets and become invisible under a filter.
    const bySource =
      sourceFilter === 'all'
        ? allItems
        : allItems.filter(i =>
            sourceFilter === 'newFrontend'
              ? i.source === 'newFrontend'
              : i.source !== 'newFrontend'
          );
    if (filter === 'all') return bySource;
    if (filter === 'inProgress') return bySource.filter(i => i.status === 'inProgress');
    if (filter === 'solved') return bySource.filter(i => i.status === 'solved' || i.solved);
    if (filter === 'rejected') return bySource.filter(i => i.status === 'rejected');
    return bySource.filter(i => (i.status === 'open' || !i.status) && !i.solved && i.status !== 'rejected');
  }, [allItems, filter, sourceFilter]);

  const sourceCounts = useMemo(() => {
    const counts = { all: allItems.length, general: 0, newFrontend: 0 };
    for (const item of allItems) {
      if ((item.source || 'general') === 'newFrontend') counts.newFrontend += 1;
      else counts.general += 1;
    }
    return counts;
  }, [allItems]);

  const visibleItems = useMemo(() => {
    const filtered = statusFilteredItems.filter(item => {
      if (priorityFilter === 'all') return true;
      if (priorityFilter === 'none') return !item.priority;
      return item.priority === priorityFilter;
    });

    if (sortMode !== 'priority') return filtered;

    const createdAtMs = item => {
      const t = new Date(item?.meta?.createdAt).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    return [...filtered].sort(
      (a, b) => priorityRank(a) - priorityRank(b) || createdAtMs(b) - createdAtMs(a)
    );
  }, [statusFilteredItems, priorityFilter, sortMode]);

  // Counts per severity for the currently selected status filter, so the team
  // sees at a glance how many P1s are waiting.
  const priorityCounts = useMemo(() => {
    const counts = { none: 0 };
    for (const key of PRIORITY_KEYS) counts[key] = 0;
    for (const item of statusFilteredItems) {
      const key = PRIORITY_CONFIG[item.priority] ? item.priority : 'none';
      counts[key] += 1;
    }
    return counts;
  }, [statusFilteredItems]);

  const priorityDialogItem = useMemo(
    () => allItems.find(i => String(i._id) === String(priorityDialogId)) || null,
    [allItems, priorityDialogId]
  );

  const handleToggleSolved = useCallback(
    async (feedbackId, nextSolved, responseText) => {
      if (!canManage) return;
      try {
        const response = typeof responseText === 'string' ? responseText.trim() : undefined;
        const updated = await setSolved({ feedbackId, solved: Boolean(nextSolved), response });
        setAllItems(prev =>
          prev.map(i => (String(i._id) === String(updated._id) ? updated : i))
        );
        if (nextSolved) {
          setExpandedItems(prev => {
            const next = new Set(prev);
            next.delete(String(feedbackId));
            return next;
          });
          setResponseTexts(prev => {
            const next = { ...prev };
            delete next[String(feedbackId)];
            return next;
          });
        }
      } catch (e) {}
    },
    [canManage, setSolved]
  );

  const handleSetResponse = useCallback(
    async feedbackId => {
      if (!canManage) return;
      try {
        const responseText = responseTexts[String(feedbackId)] || '';
        const response = typeof responseText === 'string' ? responseText.trim() : undefined;
        const updated = await setResponse({ feedbackId, response });
        setAllItems(prev =>
          prev.map(i => (String(i._id) === String(updated._id) ? updated : i))
        );
        if (response) {
          setResponseTexts(prev => ({
            ...prev,
            [String(feedbackId)]: response,
          }));
        } else {
          setResponseTexts(prev => {
            const next = { ...prev };
            delete next[String(feedbackId)];
            return next;
          });
        }

        setExpandedItems(prev => {
          const next = new Set(prev);
          next.delete(String(feedbackId));
          return next;
        });
      } catch (e) {}
    },
    [canManage, setResponse, responseTexts]
  );

  const handleSetRejected = useCallback(
    async (feedbackId, rejected) => {
      if (!canManage) return;
      try {
        const updated = await setRejected({ feedbackId, rejected: Boolean(rejected) });
        setAllItems(prev =>
          prev.map(i => (String(i._id) === String(updated._id) ? updated : i))
        );
        if (rejected) {
          setExpandedItems(prev => {
            const next = new Set(prev);
            next.delete(String(feedbackId));
            return next;
          });
          setResponseTexts(prev => {
            const next = { ...prev };
            delete next[String(feedbackId)];
            return next;
          });
        }
      } catch (e) {}
    },
    [canManage, setRejected]
  );

  const handleSetInProgress = useCallback(
    async (feedbackId, inProgress) => {
      if (!canManage) return;
      try {
        const updated = await setInProgress({ feedbackId, inProgress: Boolean(inProgress) });
        setAllItems(prev =>
          prev.map(i => (String(i._id) === String(updated._id) ? updated : i))
        );
        if (!inProgress) {
          // keep expansion as-is
          return;
        }
      } catch (e) {}
    },
    [canManage, setInProgress]
  );

  const handleOpenPriorityDialog = useCallback(item => {
    setPriorityDialogId(String(item._id));
    setPriorityDraft(item.priority || '');
  }, []);

  const handleClosePriorityDialog = useCallback(() => {
    setPriorityDialogId(null);
    setPriorityDraft('');
  }, []);

  const handleSavePriority = useCallback(async () => {
    if (!canManage || !priorityDialogItem) return;
    const note = priorityNoteRef.current?.value?.trim() || undefined;
    setIsSavingPriority(true);
    try {
      const updated = await setPriority({
        feedbackId: priorityDialogItem._id,
        priority: priorityDraft || null,
        note,
      });
      setAllItems(prev => prev.map(i => (String(i._id) === String(updated._id) ? updated : i)));
      handleClosePriorityDialog();
    } catch (e) {
    } finally {
      setIsSavingPriority(false);
    }
  }, [canManage, priorityDialogItem, priorityDraft, setPriority, handleClosePriorityDialog]);

  const handleToggleExpanded = useCallback(feedbackId => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      const idStr = String(feedbackId);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
      }
      return next;
    });
  }, []);

  // Opens/closes the reply form. When opening a fresh reply (no existing
  // response or draft), prefill the textarea with the default response template.
  const handleToggleReply = useCallback(
    item => {
      const idStr = String(item._id);
      const isExpanded = expandedItems.has(idStr);
      if (!isExpanded) {
        setResponseTexts(prev => {
          const existing = prev[idStr];
          if ((existing && existing.trim()) || item.response) return prev;
          return {
            ...prev,
            [idStr]: buildDefaultResponse(item, authCtx.currentUser?.userName),
          };
        });
      }
      handleToggleExpanded(item._id);
    },
    [expandedItems, handleToggleExpanded, authCtx.currentUser]
  );

  const handleResponseTextChange = useCallback((feedbackId, value) => {
    setResponseTexts(prev => ({
      ...prev,
      [String(feedbackId)]: value,
    }));
  }, []);

  const handleToggleReplyComposer = useCallback(feedbackId => {
    setReplyOpenItems(prev => {
      const next = new Set(prev);
      const idStr = String(feedbackId);
      if (next.has(idStr)) {
        next.delete(idStr);
      } else {
        next.add(idStr);
      }
      return next;
    });
  }, []);

  const handleReplyTextChange = useCallback((feedbackId, value) => {
    setReplyTexts(prev => ({
      ...prev,
      [String(feedbackId)]: value,
    }));
  }, []);

  // Appends a manager reply to the conversation thread (separate from editing
  // the primary response above).
  const handleSendReplyMessage = useCallback(
    async feedbackId => {
      if (!canManage) return;
      const text = (replyTexts[String(feedbackId)] || '').trim();
      if (!text) return;
      try {
        const updated = await addResponseMessage({ feedbackId, text });
        setAllItems(prev =>
          prev.map(i => (String(i._id) === String(updated._id) ? updated : i))
        );
        setReplyTexts(prev => {
          const next = { ...prev };
          delete next[String(feedbackId)];
          return next;
        });
        setReplyOpenItems(prev => {
          const next = new Set(prev);
          next.delete(String(feedbackId));
          return next;
        });
      } catch (e) {}
    },
    [canManage, addResponseMessage, replyTexts]
  );

  if (!canRead) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 2, maxWidth: 900, width: '100%', mx: 'auto' }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant='h6'>Feedback</Typography>
          <Typography variant='body2' color='text.secondary'>
            Du hast keine Berechtigung, Feedback anzusehen.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 1, sm: 2 }, maxWidth: 1100, width: '100%', mx: 'auto' }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={1.5}>
          {readOnly && (
            <Paper
              variant='outlined'
              sx={t => ({
                p: 1,
                backgroundColor: alpha(t.palette.info.main, 0.1),
                borderColor: alpha(t.palette.info.main, 0.4),
              })}
            >
              <Typography variant='body2' color='info.main'>
                Nur-Lese-Modus: Du siehst das Feedback deiner zugeordneten Standorte und kannst es
                nicht bearbeiten.
              </Typography>
            </Paper>
          )}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setFilter(v);
              }}
              size='small'
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexWrap: 'wrap',
                '& .MuiToggleButtonGroup-grouped': {
                  flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 auto' },
                  minWidth: { xs: 0, sm: 92 },
                  border: theme => ({ xs: `1px solid ${theme.palette.divider}`, sm: undefined }),
                  marginLeft: { xs: '0 !important', sm: undefined },
                  marginTop: { xs: '0 !important', sm: undefined },
                },
              }}
            >
              <ToggleButton value='open'>Offen</ToggleButton>
              <ToggleButton value='inProgress'>In Arbeit</ToggleButton>
              <ToggleButton value='solved'>Gelöst</ToggleButton>
              <ToggleButton value='rejected'>Abgelehnt</ToggleButton>
              <ToggleButton value='all'>Alle</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel id='feedback-priority-filter-label'>Priorität</InputLabel>
              <Select
                labelId='feedback-priority-filter-label'
                label='Priorität'
                value={priorityFilter}
                onChange={e => setPriorityFilter(e.target.value)}
              >
                <MenuItem value='all'>Alle Prioritäten</MenuItem>
                {PRIORITY_KEYS.map(key => (
                  <MenuItem key={key} value={key}>
                    {`${priorityLabel(key)} (${priorityCounts[key]})`}
                  </MenuItem>
                ))}
                <MenuItem value='none'>{`Ohne Priorität (${priorityCounts.none})`}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 170 } }}>
              <InputLabel id='feedback-sort-label'>Sortierung</InputLabel>
              <Select
                labelId='feedback-sort-label'
                label='Sortierung'
                value={sortMode}
                onChange={e => setSortMode(e.target.value)}
              >
                <MenuItem value='priority'>Nach Priorität</MenuItem>
                <MenuItem value='date'>Nach Datum</MenuItem>
              </Select>
            </FormControl>

            <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <InputLabel id='feedback-source-filter-label'>Herkunft</InputLabel>
              <Select
                labelId='feedback-source-filter-label'
                label='Herkunft'
                value={sourceFilter}
                onChange={e => setSourceFilter(e.target.value)}
              >
                <MenuItem value='all'>{`Alle Herkünfte (${sourceCounts.all})`}</MenuItem>
                <MenuItem value='general'>{`Allgemein (${sourceCounts.general})`}</MenuItem>
                <MenuItem value='newFrontend'>{`Neues Management (${sourceCounts.newFrontend})`}</MenuItem>
              </Select>
            </FormControl>

            <IconButton
              onClick={() => load().catch(() => {})}
              disabled={isLoading}
              sx={{
                ml: { xs: 0, sm: 'auto' },
                alignSelf: { xs: 'stretch', sm: 'center' },
                width: { xs: '100%', sm: 'auto' },
                borderRadius: 1,
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Stack>

          {isLoading ? (
            <Typography variant='body2' color='text.secondary'>
              Lädt…
            </Typography>
          ) : visibleItems.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Kein Feedback vorhanden.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {visibleItems.map(item => {
                const cfg = RATING_CONFIG[item.rating];
                const Icon = cfg?.Icon;
                const viewLabel = viewLabelByKey[item.viewKey] || item.viewKey || '-';
                const userLabel =
                  item?.user?.name || item?.user?.username || item?.user?.email || '-';
                const branchLabel =
                  item?.user?.assignedBranches
                    ?.map(branch => branch?.name)
                    .filter(Boolean)
                    .join(', ') || 'Standort unbekannt';
                const isExpanded = expandedItems.has(String(item._id));
                const status = item.status || (item.solved ? 'solved' : 'open');
                const isSolved = status === 'solved';
                const isRejected = status === 'rejected';
                const isOpen = status === 'open';
                const isInProgress = status === 'inProgress';
                const responseText = responseTexts[String(item._id)] ?? item.response ?? '';
                const messages = Array.isArray(item.messages) ? item.messages : [];
                // A conversation exists once a primary answer was given or a
                // thread message was posted; only then can managers add follow-ups.
                const hasConversation = Boolean(item.response) || messages.length > 0;
                const isReplyOpen = replyOpenItems.has(String(item._id));
                const replyText = replyTexts[String(item._id)] ?? '';

                return (
                  <Paper key={item._id} variant='outlined' sx={{ p: { xs: 1, sm: 1.5 } }}>
                    <Stack spacing={0.75}>
                      <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', lg: 'center' }}
                        justifyContent='space-between'
                      >
                        <Stack
                          direction='column'
                          spacing={0.75}
                          sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}
                        >
                          <Stack direction='row' spacing={1} alignItems='flex-start' sx={{ minWidth: 0 }}>
                            {Icon ? (
                              <Icon
                                sx={{
                                  color: cfg.color,
                                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                  mt: 0.25,
                                  flexShrink: 0,
                                }}
                              />
                            ) : null}
                            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant='subtitle2'
                                sx={{
                                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                  overflowWrap: 'anywhere',
                                }}
                              >
                                {viewLabel}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              >
                                {formatDateTime(item?.meta?.createdAt)}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                            <Tooltip title={priorityLabel(item.priority)}>
                              <Chip
                                size='small'
                                label={
                                  PRIORITY_CONFIG[item.priority]
                                    ? PRIORITY_CONFIG[item.priority].label
                                    : 'Ohne Prio'
                                }
                                variant={PRIORITY_CONFIG[item.priority] ? 'filled' : 'outlined'}
                                onClick={() => handleOpenPriorityDialog(item)}
                                sx={priorityChipSx(item.priority)}
                              />
                            </Tooltip>
                            <Chip
                              size='small'
                              label={userLabel}
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, maxWidth: '100%' }}
                            />
                            {item?.source === 'newFrontend' ? (
                              <Chip
                                size='small'
                                variant='outlined'
                                color='primary'
                                label={`Neues Management${item?.newFrontend?.viewTitle ? ` · ${item.newFrontend.viewTitle}` : ''}`}
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, maxWidth: '100%' }}
                              />
                            ) : null}
                            {item?.bookingNumber ? (
                              <Chip
                                size='small'
                                label={`Buchung #${item.bookingNumber}`}
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, maxWidth: '100%' }}
                              />
                            ) : null}
                            {isSolved ? (
                              <Chip
                                size='small'
                                icon={<DoneAllIcon />}
                                label='Gelöst'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.success.main, 0.2),
                                  color: 'success.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            ) : isRejected ? (
                              <Chip
                                size='small'
                                icon={<CancelIcon />}
                                label='Abgelehnt'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.error.main, 0.2),
                                  color: 'error.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            ) : isInProgress ? (
                              <Chip
                                size='small'
                                label='In Arbeit'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.info.main, 0.15),
                                  color: 'info.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            ) : (
                              <Chip
                                size='small'
                                label='Offen'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.warning.main, 0.15),
                                  color: 'warning.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            )}
                            {item.forwardedIssueId ? (
                              <Chip
                                size='small'
                                label='An IT weitergeleitet'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.info.main, 0.15),
                                  color: 'info.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            ) : null}
                            {item.adminUnread ? (
                              <Chip
                                size='small'
                                label='Neue Antwort'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.secondary.main, 0.2),
                                  color: 'secondary.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            ) : null}
                            {item.unread ? (
                              <Chip
                                size='small'
                                label='Ungelesen'
                                sx={t => ({
                                  backgroundColor: alpha(t.palette.secondary.main, 0.2),
                                  color: 'secondary.main',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                })}
                              />
                            ) : item.readAt ? (
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, alignSelf: 'center' }}
                              >
                                Gelesen {formatDateTime(item.readAt)}
                              </Typography>
                            ) : null}
                          </Stack>

                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, overflowWrap: 'anywhere' }}
                          >
                            Standort: {branchLabel}
                          </Typography>
                        </Stack>

                        {canManage && (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          useFlexGap
                          flexWrap='wrap'
                          sx={{
                            width: { xs: '100%', lg: 'auto' },
                            mt: { xs: 0.5, lg: 0 },
                            flexShrink: 0,
                            justifyContent: { xs: 'stretch', sm: 'flex-end' },
                          }}
                        >
                          <Button
                            size='small'
                            variant='outlined'
                            color='inherit'
                            onClick={() => handleOpenPriorityDialog(item)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                          >
                            Priorität
                          </Button>
                          {isOpen && (
                            <Button
                              size='small'
                              variant='outlined'
                              color='info'
                              onClick={() => handleSetInProgress(item._id, true)}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                            >
                              In Arbeit
                            </Button>
                          )}
                          {isInProgress && (
                            <Button
                              size='small'
                              variant='outlined'
                              color='warning'
                              onClick={() => handleSetInProgress(item._id, false)}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                            >
                              Zurück auf offen
                            </Button>
                          )}
                          {(isOpen || isInProgress) && (
                            <Button
                              size='small'
                              variant='outlined'
                              color='error'
                              onClick={() => handleSetRejected(item._id, true)}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                            >
                              Ablehnen
                            </Button>
                          )}
                          {(isOpen || isInProgress) && !item.forwardedIssueId && (
                            <Button
                              size='small'
                              variant='outlined'
                              color='info'
                              onClick={() => setForwardItem(item)}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                            >
                              An IT weiterleiten
                            </Button>
                          )}
                          <Button
                            size='small'
                            variant='outlined'
                            color='warning'
                            onClick={() => handleToggleReply(item)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                          >
                            {isExpanded ? 'Antwort ausblenden' : item.response ? 'Antwort bearbeiten' : 'Antworten'}
                          </Button>
                          {(isOpen || isInProgress) && (
                            <Button
                              size='small'
                              variant='contained'
                              color='success'
                              onClick={() => handleToggleSolved(item._id, true, responseText)}
                              disabled={isExpanded && !responseText.trim()}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                            >
                              Als gelöst markieren
                            </Button>
                          )}
                          {(isSolved || isRejected) && (
                            <Button
                              size='small'
                              variant={isSolved ? 'contained' : 'outlined'}
                              color={isSolved ? 'success' : 'error'}
                              onClick={() => {
                                if (isSolved) {
                                  handleToggleSolved(item._id, false);
                                } else {
                                  handleSetRejected(item._id, false);
                                }
                              }}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
                            >
                              {isSolved ? 'Gelöst' : 'Abgelehnt'}
                            </Button>
                          )}
                        </Stack>
                        )}
                      </Stack>

                      <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {item.text || 'Kein Text'}
                      </Typography>

                      <FeedbackScreenshots
                        feedbackId={String(item?._id || '')}
                        screenshots={item?.screenshots}
                      />

                      {item.response && !isExpanded && (
                        <Paper variant='outlined' sx={{ p: 1, backgroundColor: 'action.hover' }}>
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            Antwort:
                          </Typography>
                          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mt: 0.5, overflowWrap: 'anywhere' }}>
                            {item.response}
                          </Typography>
                        </Paper>
                      )}

                      {canManage && isExpanded && (
                        <Stack spacing={1}>
                          <TextField
                            label='Antwort'
                            placeholder='Schreibe eine Antwort auf das Feedback...'
                            multiline
                            minRows={3}
                            maxRows={10}
                            value={responseText}
                            onChange={e => handleResponseTextChange(item._id, e.target.value)}
                            fullWidth
                            size='small'
                          />
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent='flex-end'
                          >
                            <Button
                              size='small'
                              variant='outlined'
                              fullWidth
                              onClick={() => {
                                handleResponseTextChange(item._id, item.response || '');
                                handleToggleExpanded(item._id);
                              }}
                            >
                              Abbrechen
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              fullWidth
                              onClick={() => handleSetResponse(item._id)}
                            >
                              Antwort speichern
                            </Button>
                            <Button
                              size='small'
                              variant='contained'
                              color='success'
                              onClick={() => handleToggleSolved(item._id, true, responseText)}
                              disabled={!responseText.trim()}
                              fullWidth
                            >
                              Als gelöst markieren
                            </Button>
                          </Stack>
                        </Stack>
                      )}

                      {messages.length > 0 && (
                        <Stack spacing={0.75}>
                          {messages.map((msg, idx) => {
                            const isAdmin = msg.sender === 'admin';
                            return (
                              <Paper
                                key={msg._id || idx}
                                variant='outlined'
                                sx={{
                                  p: 1,
                                  backgroundColor: isAdmin ? 'action.hover' : 'transparent',
                                }}
                              >
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {isAdmin ? msg.authorName || 'Team' : msg.authorName || userLabel}
                                  {msg.createdAt ? ` · ${formatDateTime(msg.createdAt)}` : ''}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  sx={{ whiteSpace: 'pre-wrap', mt: 0.5, overflowWrap: 'anywhere' }}
                                >
                                  {msg.text}
                                </Typography>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}

                      {canManage &&
                        hasConversation &&
                        (isReplyOpen ? (
                          <Stack spacing={1}>
                            <TextField
                              label='Antwort im Verlauf'
                              placeholder='Antwort an den Nutzer schreiben…'
                              multiline
                              minRows={2}
                              maxRows={10}
                              value={replyText}
                              onChange={e => handleReplyTextChange(item._id, e.target.value)}
                              fullWidth
                              size='small'
                            />
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              justifyContent='flex-end'
                            >
                              <Button
                                size='small'
                                variant='outlined'
                                fullWidth
                                onClick={() => {
                                  handleReplyTextChange(item._id, '');
                                  handleToggleReplyComposer(item._id);
                                }}
                              >
                                Abbrechen
                              </Button>
                              <Button
                                size='small'
                                variant='contained'
                                fullWidth
                                onClick={() => handleSendReplyMessage(item._id)}
                                disabled={!replyText.trim()}
                              >
                                Antwort senden
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Stack direction='row' justifyContent='flex-end'>
                            <Button
                              size='small'
                              variant='outlined'
                              color={item.adminUnread ? 'secondary' : 'primary'}
                              onClick={() => handleToggleReplyComposer(item._id)}
                            >
                              {item.adminUnread ? 'Auf Antwort reagieren' : 'Im Verlauf antworten'}
                            </Button>
                          </Stack>
                        ))}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Paper>

      <ForwardFeedbackToIssueDialog
        item={forwardItem}
        onClose={() => setForwardItem(null)}
        onForwarded={updated => {
          if (updated?._id) {
            setAllItems(prev => prev.map(i => (String(i._id) === String(updated._id) ? updated : i)));
          } else {
            load().catch(() => {});
          }
        }}
      />

      <Dialog
        open={Boolean(priorityDialogItem)}
        onClose={handleClosePriorityDialog}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>{canManage ? 'Priorität festlegen' : 'Priorität'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size='small' fullWidth disabled={!canManage}>
              <InputLabel id='feedback-priority-select-label'>Priorität</InputLabel>
              <Select
                labelId='feedback-priority-select-label'
                label='Priorität'
                value={priorityDraft}
                onChange={e => setPriorityDraft(e.target.value)}
              >
                <MenuItem value=''>Keine Priorität</MenuItem>
                {PRIORITY_KEYS.map(key => (
                  <MenuItem key={key} value={key}>
                    {priorityLabel(key)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {canManage && (
              <TextField
                label='Begründung (optional)'
                placeholder='Warum wird die Priorität geändert?'
                inputRef={priorityNoteRef}
                defaultValue=''
                key={String(priorityDialogItem?._id || 'none')}
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                size='small'
                inputProps={{ maxLength: 500 }}
              />
            )}

            <Divider />

            <Typography variant='subtitle2'>Verlauf</Typography>
            {Array.isArray(priorityDialogItem?.priorityHistory) &&
            priorityDialogItem.priorityHistory.length > 0 ? (
              <Stack spacing={0.75}>
                {[...priorityDialogItem.priorityHistory]
                  .slice()
                  .reverse()
                  .map((entry, idx) => (
                    <Paper key={entry._id || idx} variant='outlined' sx={{ p: 1 }}>
                      <Typography variant='body2'>
                        {`${PRIORITY_CONFIG[entry.from]?.label || 'Ohne Priorität'} → ${
                          PRIORITY_CONFIG[entry.to]?.label || 'Ohne Priorität'
                        }`}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {`${entry.changedByName || 'Unbekannt'} · ${formatDateTime(entry.changedAt)}`}
                      </Typography>
                      {entry.note ? (
                        <Typography
                          variant='body2'
                          sx={{ whiteSpace: 'pre-wrap', mt: 0.5, overflowWrap: 'anywhere' }}
                        >
                          {entry.note}
                        </Typography>
                      ) : null}
                    </Paper>
                  ))}
              </Stack>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Noch keine Priorität vergeben.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePriorityDialog}>{canManage ? 'Abbrechen' : 'Schließen'}</Button>
          {canManage && (
            <Button
              variant='contained'
              onClick={handleSavePriority}
              disabled={
                isSavingPriority || (priorityDraft || '') === (priorityDialogItem?.priority || '')
              }
            >
              Speichern
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

